import React, { useMemo, useEffect, useState } from 'react';
import { Select, Space, Button, Typography } from 'antd';
import { gltfLoaderWithDraco } from '../../../lib/draco-loader/DracoLoader';
import { ACTION_PREVIEW_EVENT } from '../../actions-preview/ActionPreview';
import styles from '../../../event-timeline.module.css';
import type { ActionType } from '@/components/generated/graphql';

const { Option } = Select;
const { Text } = Typography;

export type Params = {
  modelURL: string;
  loop: boolean;
  selectedAnimationName: string;
};

interface PlayAnimationParamsProps {
  params: Params;
  selectableAssets: any[];
  data: {
    type: ActionType;
    params: Params;
    delay: number;
    duration: number;
  };
  target: {
    objectId: string;
    previewImage: string;
  };
  setParams: (params: any) => void;
}

const PlayAnimationParams: React.FC<PlayAnimationParamsProps> = ({
  target,
  data,
  params,
  setParams,
  selectableAssets,
}) => {
  const [animations, setAnimations] = useState<string[]>([]);

  const targetObjectURL = useMemo(() => {
    if (!target || !selectableAssets.length) return '';
    let url = '';

    selectableAssets.forEach((asset) => {
      if (asset.props.asset.id === target?.objectId) {
        url = asset.props.asset.url;
      }
    });

    return url;
  }, [target, selectableAssets.length]);

  function onOpenPreview() {
    window.dispatchEvent(
      new CustomEvent(ACTION_PREVIEW_EVENT, {
        detail: {
          action: {
            ...data,
            modelURL: targetObjectURL,
          },
        },
      })
    );
  }

  useEffect(() => {
    (async () => {
      if (!targetObjectURL) return;

      gltfLoaderWithDraco.load(targetObjectURL, (gltf) => {
        if (gltf.animations && gltf.animations.length > 0) {
          gltf.animations.forEach((anim) => {
            setAnimations((prev) => [...prev, anim.name]);
          });
        }
      });
    })();
  }, [gltfLoaderWithDraco, targetObjectURL]);

  return (
    <div className={styles.parameterContainer}>
      <Space direction='vertical' style={{ width: '100%' }}>
        {!targetObjectURL && (
          <Text type='danger'>
            <span>Target not found </span>
            <p>Add Target node select the object and connect.</p>
          </Text>
        )}
        {targetObjectURL && (
          <>
            <Button
              onClick={onOpenPreview}
              className={styles.previewActionButton}
            >
              Preview Animations
            </Button>
            <span className={styles.label}>Select Animation:</span>
            <Select
              value={params.selectedAnimationName}
              onChange={(value) =>
                setParams({ ...params, selectedAnimationName: value })
              }
              style={{ width: '100%' }}
            >
              {animations.map((animName, i) => {
                return (
                  <Option value={animName} key={`anim-${i}`}>
                    {animName}
                  </Option>
                );
              })}
            </Select>
          </>
        )}
        <span className={styles.label}>Loop:</span>
        <Select
          value={params.loop ? 'true' : 'false'}
          onChange={(value) => setParams({ ...params, loop: value === 'true' })}
          style={{ width: '100%' }}
        >
          <Option value='true'>Yes</Option>
          <Option value='false'>No</Option>
        </Select>
      </Space>
    </div>
  );
};

export default PlayAnimationParams;
